/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import PropTypes from "prop-types";
import Timeago from "react-timeago";
import { SocialShare } from "@quintype/components";
import { SocialShareTemplate } from "../../../Molecules/SocialShareTemplate";
import { SectionTag } from "../../../Atoms/SectionTag";
import { Subheadline } from "../../../Atoms/Subheadline";
import AsideCollection from "../../AsideCollection";
import { AuthorCard } from "../../../Atoms/AuthorCard";
import { StoryElementCard } from "../../../Molecules/StoryElementCard";
import { PublishDetails } from "../../../Atoms/PublishDetail";
import { StoryTags } from "../../../Atoms/StoryTags";
import { HeroImage } from "../../../Atoms/HeroImage";
import "./live-blog.m.css";
import { CaptionAttribution } from "../../../Atoms/CaptionAttribution";
import { clientWidth, getTextColor, timestampFirstFormatter } from "../../../../utils/utils";
import LiveIcon from "../../../Svgs/liveicon";
import KeyEvents from "../../../Molecules/KeyEvents";
import { ClockIcon } from "../../../Svgs/clock-icon";
import { StoryHeadline } from "../../../Atoms/StoryHeadline";

export const LiveBlogStoryTemplates = ({
  story = {},
  config = {},
  children,
  storyElementsConfig,
  adComponent,
  widgetComp
}) => {
  const {
    theme = "",
    asideCollection = {},
    templateType,
    noOfVisibleCards = 0,
    publishedDetails = {},
    verticalShare = "",
    shareIconType = "plain-color-svg",
    authorDetails = {
      template: "default"
    },
    premiumStoryIconConfig = {}
  } = config;

  const visibleCards = noOfVisibleCards < 0 ? story.cards : story.cards.slice(0, noOfVisibleCards);
  const textColor = getTextColor(theme);
  const dark = "#333";
  const light = "#fff";
  const updateColor = textColor === "dark" ? dark : light;

  const isMobile = clientWidth("mobile");

  const HeaderCard = () => {
    return (
      <div styleName="header-wrapper header-details">
        <SectionTag story={story} />
        <div styleName="live-headline">
          <div styleName="live-icon">
            <LiveIcon />
          </div>
          <StoryHeadline story={story} premiumStoryIconConfig={premiumStoryIconConfig} />
        </div>
        <Subheadline story={story} showFullContent />
      </div>
    );
  };

  const StoryCards = () => {
    return visibleCards.map((card, index) => {
      const borderBottom = index === visibleCards.length - 1 ? "" : `share-cards ${textColor}`;
      return (
        <div key={index} styleName={borderBottom}>
          <div styleName="card-share">
            <div styleName={`time-wrapper ${textColor}`}>
              <ClockIcon color={updateColor} />
              <Timeago date={card["card-added-at"]} formatter={timestampFirstFormatter} />
            </div>
            <SocialShare
              template={SocialShareTemplate}
              fullUrl={story.url}
              title={story.headline}
              theme={theme}
              iconType={shareIconType}
            />
          </div>
          <StoryElementCard
            story={story}
            card={card}
            key={card.id}
            config={storyElementsConfig}
            isLive
            theme={theme}
            adComponent={adComponent}
            widgetComp={widgetComp}
          />
        </div>
      );
    });
  };

  const keyEvents = () => {
    return (
      <div styleName="key-events">
        <KeyEvents story={story} showLoadMore={true} config={config} />
      </div>
    );
  };

  const VerticalShare = () => {
    return (
      <SocialShare
        template={SocialShareTemplate}
        fullUrl={story.url}
        title={story.headline}
        vertical={true}
        theme={theme}
        iconType={shareIconType}
      />
    );
  };

  const showKeyEvents = () => {
    if ((templateType === "default" || templateType === "headline-priority") && !isMobile) {
      return null;
    }
    return keyEvents();
  };

  const StoryData = () => {
    return (
      <>
        <AuthorCard clazzName="gap-32" story={story} template={authorDetails.template} opts={authorDetails.opts} />
        <div styleName="timestamp-social-share">
          <PublishDetails story={story} opts={publishedDetails} />
          {!verticalShare && (
            <SocialShare
              template={SocialShareTemplate}
              fullUrl={story.url}
              title={story.headline}
              theme={theme}
              iconType={shareIconType}
            />
          )}
        </div>
        {showKeyEvents()}
        <StoryCards />
        <StoryTags tags={story.tags} />
        <div styleName="children">{children}</div>
      </>
    );
  };

  const DefaultTemplate = story => {
    return (
      <>
        <div data-test-id="hero-image" styleName="grid-col-full index-2">
          <HeroImage story={story} aspectRatio={[[16, 9], [8, 3]]} isStoryPageImage />
        </div>
        <div styleName="header-wrapper">
          <CaptionAttribution story={story} config={config} />
          <HeaderCard />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <div styleName="side-column">
          {asideCollection && (
            <AsideCollection
              {...asideCollection}
              adComponent={adComponent}
              widgetComp={widgetComp}
              sticky={true}
              enableKeyEvents={!isMobile}
              keyEventsData={{ story, config, showLoadMore: false }}
            />
          )}
        </div>
      </>
    );
  };

  const HeroOverlay = story => {
    return (
      <>
        <div styleName="overlay-hero  index-2">
          <HeroImage story={story} aspectRatio={[[1, 2], [16, 9]]} isStoryPageImage />
        </div>
        <div styleName="overlay-grid">
          <HeaderCard />
        </div>
        <div styleName="grid-col-4-12 ">
          <CaptionAttribution story={story} config={config} />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        {asideCollection && (
          <div styleName="grid-container tablet-index-2">
            <AsideCollection horizontal={true} {...asideCollection} adComponent={adComponent} widgetComp={widgetComp} />
          </div>
        )}
      </>
    );
  };

  const HeadlineSideway = story => {
    return (
      <>
        <div styleName="grid-col-2-9 sideway ">
          <HeaderCard />
        </div>
        <div styleName="grid-col-9-15 index-2">
          <HeroImage story={story} aspectRatio={[[4, 3]]} isStoryPageImage />
          <CaptionAttribution story={story} config={config} />
        </div>
        <div styleName="grid-col-4-12 ">
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        {asideCollection && (
          <div styleName="grid-container tablet-index-2">
            <AsideCollection horizontal={true} {...asideCollection} adComponent={adComponent} widgetComp={widgetComp} />
          </div>
        )}
      </>
    );
  };

  const HeadlinePriority = story => {
    return (
      <>
        <div styleName="grid-col-2-9  grid-row-1-2 headline-space">
          <HeaderCard />
        </div>
        <div styleName="grid-col-2-9  grid-row-2-3 index-2">
          <HeroImage story={story} aspectRatio={[[16, 9]]} isStoryPageImage />
        </div>
        <div styleName="grid-col-2-9  grid-row-3-4">
          <CaptionAttribution story={story} config={config} />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <div styleName="grid-col-9-14 grid-row-1-6">
          {asideCollection && (
            <AsideCollection
              {...asideCollection}
              adComponent={adComponent}
              widgetComp={widgetComp}
              sticky={true}
              enableKeyEvents={!isMobile}
              keyEventsData={{ story, config, showLoadMore: false }}
            />
          )}
        </div>
      </>
    );
  };

  const HeroPriority = story => {
    return (
      <>
        <div styleName="grid-container index-2">
          <HeroImage story={story} aspectRatio={[[16, 9]]} isStoryPageImage />
        </div>
        <div styleName="grid-col-4-12 ">
          <CaptionAttribution story={story} config={config} />
          <HeaderCard />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        {asideCollection && (
          <div styleName="grid-container tablet-index-2">
            <AsideCollection horizontal={true} {...asideCollection} adComponent={adComponent} widgetComp={widgetComp} />
          </div>
        )}
      </>
    );
  };

  const HeadlineHeroPriority = story => {
    return (
      <>
        <div styleName="grid-col-4-12 headline-space">
          <HeaderCard />
        </div>
        <div styleName="grid-container  index-2">
          <HeroImage story={story} aspectRatio={[[16, 9]]} isStoryPageImage />
        </div>
        <div styleName="grid-col-4-12 ">
          <CaptionAttribution story={story} config={config} />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        {asideCollection && (
          <div styleName="grid-container tablet-index-2">
            <AsideCollection horizontal={true} {...asideCollection} adComponent={adComponent} widgetComp={widgetComp} />
          </div>
        )}
      </>
    );
  };

  const getLiveBlogStoryTemplates = templateType => {
    switch (templateType) {
      case "hero-priority":
        return HeroPriority(story);
      case "hero-overlay":
        return HeroOverlay(story);
      case "headline-sideway":
        return HeadlineSideway(story);
      case "headline-priority":
        return HeadlinePriority(story);
      case "headline-hero-priority":
        return HeadlineHeroPriority(story);
      default:
        return DefaultTemplate(story);
    }
  };

  return <>{getLiveBlogStoryTemplates(templateType)}</>;
};

LiveBlogStoryTemplates.propTypes = {
  story: PropTypes.object,
  config: PropTypes.shape({
    templateType: PropTypes.string,
    authorCard: PropTypes.object,
    asideCollection: PropTypes.object
  }),
  children: PropTypes.node,
  storyElementsConfig: PropTypes.object,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func
};
